import React from 'react';
import moment from 'moment';
import IdNameEntity from './IdNameEntity';
import PopulationEntity from './PopulationEntity';
import { DOCUMENT_TYPOLOGY } from '../utils/constants';
import { formatDateShort, formatMonth } from '../utils/date-utils';
import DocumentAttachmentEntity from './DocumentAttachmentEntity';
import AuditEntity from './AuditEntity';

export default class DocumentEntity {

  static statuses = new Map([
    ['preparing', 'En préparation'],
    ['future', 'À venir'],
    ['past', 'Archivé'],
    ['current', 'Publié'],
  ]);

  static userShape = {
    id: React.PropTypes.string.isRequired,
    firstName: React.PropTypes.string.isRequired,
    lastName: React.PropTypes.string.isRequired,
  };

  static shape = {
    id: React.PropTypes.string.isRequired,
    categories: React.PropTypes.arrayOf(React.PropTypes.shape(IdNameEntity.shape)).isRequired,
    startDate: React.PropTypes.string, // yyyy-MM-dd
    endDate: React.PropTypes.string, // yyyy-MM-dd
    displayMonth: React.PropTypes.string, // yyyy-MM
    subCategory: React.PropTypes.string,
    title: React.PropTypes.string.isRequired,
    type: React.PropTypes.oneOf(DocumentAttachmentEntity.types),
    fileName: React.PropTypes.string.isRequired,
    createdBy: React.PropTypes.shape(DocumentEntity.userShape),
    highPriority: React.PropTypes.bool.isRequired,
    audit: React.PropTypes.shape(AuditEntity.shape),
    loading: React.PropTypes.bool,
    updating: React.PropTypes.bool,
    populations: React.PropTypes.arrayOf(React.PropTypes.shape(PopulationEntity.shape)),
    attachments: React.PropTypes.arrayOf(React.PropTypes.shape(DocumentAttachmentEntity.shape)),
  };

  /**
   *
   * @type {Array<PopulationEntity>}
   */
  populations = [];

  /**
   * Returns the default start date for a new document.
   */
  static getDefaultStartDate() {
    return moment();
  }

  constructor(document) {
    if (document) {
      Object.assign(this, document);
      this.populations = (document.populations || [])
        .map(population => new PopulationEntity(population));
      this.attachments = (document.attachments || [])
        .map(attachment => new DocumentAttachmentEntity(attachment, this));
    } else {
      const defaultStartDate = DocumentEntity.getDefaultStartDate();
      const startDate = defaultStartDate
        .format('YYYY-MM-DD');
      const endDate = defaultStartDate.clone()
        .add(1, 'months')
        .add(-1, 'days')
        .format('YYYY-MM-DD');
      const displayMonth = defaultStartDate.clone()
        .date(1)
        .format('YYYY-MM');
      const populations = [];
      const attachments = [];
      const subCategory = '';
      Object.assign(this, { startDate, endDate, displayMonth, populations, attachments, subCategory });
    }
  }

  asHistory() {
    this.history = true;
    return this;
  }

  static validate = (formValues) => {
    const errors = {};
    if (!formValues) {
      return errors;
    }

    formValues.populations.forEach((population, index) => {
      let error = null;
      if (!population.type) {
        error = {
          users: 'Au moins un utilisateur ou une typologie',
          typology: 'Une typologie ou au moins un utilisateur',
        };
      } else if (population.typology === DOCUMENT_TYPOLOGY.REGIONAL && (population.regions || []).length === 0) {
        error = {
          regions: 'Au moins une région',
        };
      } else if (population.typology === DOCUMENT_TYPOLOGY.AGENCY && (population.agencies || []).length === 0) {
        error = {
          agencies: 'Au moins une zone de vente',
        };
      }

      if (error) {
        if (!errors.populations) {
          errors.populations = [];
        }
        errors.populations[index] = error;
      }
    });

    formValues.attachments.forEach((attachment, index) => {
      const error = DocumentAttachmentEntity.validate(attachment);
      if (error) {
        if (!errors.attachments) {
          errors.attachments = [];
        }
        errors.attachments[index] = error;
      }
    });

    if (formValues.startDate && formValues.endDate) {
      if (formValues.startDate > formValues.endDate) {
        errors.startDate = 'La date de début doit être après la date de fin';
        errors.endDate = 'La date de fin doit être avant la date de début';
      }
    }

    if (!formValues.title) {
      errors.title = 'Titre obligatoire';
    }

    if (!formValues.file) {
      errors.file = 'Document obligatoire';
    } else if (formValues.file.typeLink) {
      if (!formValues.file.name) {
        errors.file = 'Lien obligatoire';
      } else if (!formValues.file.name.match(/^https?:\/\/.+$/i)) {
        errors.file = 'Lien invalide';
      }
    } else if (formValues.file.size > 35000000) {
      errors.file = 'Taille limite du document : 35Mo';
    }

    if (!(formValues.categories && formValues.categories.length)) {
      errors.categories = 'Au moins une rubrique';
    }

    return errors;
  };

  get status() {
    if (!this.startDate || !this.endDate) {
      return 'preparing';
    }
    const now = moment().format('YYYY-MM-DD');
    if (now < this.startDate) {
      return 'future';
    }
    if (now > this.endDate) {
      return 'past';
    }
    return 'current';
  }

  get statusName() {
    return DocumentEntity.statuses.get(this.status);
  }

  get typologies() {
    return this.populations
      .map(population => population.typology)
      .filter(typology => typology !== null)
      .reduce(
        (acc, typology) => {
          if (acc.indexOf(typology) < 0) {
            acc.push(typology);
          }
          return acc;
        }, []
      );
  }

  get categoryNames() {
    if (!this.categories) {
      return '';
    }
    return this.categories.map(category => category.name).join(', ');
  }

  get firstCategoryRank() {
    if (!this.categories) {
      return -1;
    }
    return Math.min(...this.categories.map(cat => cat.rank));
  }

  get authorName() {
    if (!this.createdBy) {
      return '';
    }
    return `${this.createdBy.lastName} ${this.createdBy.firstName}`;
  }

  get formattedStartDate() {
    return formatDateShort(this.startDate);
  }

  get formattedEndDate() {
    return formatDateShort(this.endDate);
  }

  get formattedMonth() {
    return formatMonth(this.displayMonth);
  }

  get subCategoryName() {
    return this.displayMonth ? this.formattedMonth : this.subCategory;
  }

  get isNew() {
    return this.startDate && moment().diff(this.startDate, 'weeks') === 0;
  }

  get isUpdated() {
    return this.audit && this.audit.creationDateTime && this.audit.updateDateTime && this.startDate
      && this.audit.creationDateTime !== this.audit.updateDateTime
      && moment(this.audit.updateDateTime).isAfter(this.startDate)
      && moment().diff(this.audit.updateDateTime, 'weeks') === 0;
  }

  get attachmentUrl() {
    return this.type === 'LINK' ?
      this.fileName :
      this.id && this.fileName && `/api/documents/${this.id}/${encodeURIComponent(this.fileName)}`;
  }

  getAttachmentUrl(attachmentId) {
    if (attachmentId) {
      const attachment = this.attachments.find(att => att.id === attachmentId);
      return attachment && attachment.type === 'LINK' ?
        attachment.fileName :
        `/api/documents/${this.id}/${attachmentId}/${encodeURIComponent(attachment.fileName)}`;
    }
    return this.attachmentUrl;
  }
}



// WEBPACK FOOTER //
// ./shared/src/entity/DocumentEntity.js